import { FpAxios } from '@/services/FpAxios';
import { QuizCategoryInfo, QuizCategoryInfoDTO, QuizTest, QuizTestAttempt, QuizTestBody, QuizUser } from '@/modules/bhp/models/QuizTest';
import { MandateContractUserFilters, QuizAttemptFilter } from '@/models/Filter';
import { MandateContractList } from '@/models/MandateContract';
import { QuizCategory } from '@/models/Quiz';
import axios from 'axios';
import { QuizList } from '@/models/QuizList';

class HumanResourcesApi {
  private fpAxios = FpAxios.create();
  private readonly quizUrl: string = 'quiz';
  private readonly usersUrl: string = 'users';
  private CancellToken = axios.CancelToken;
  private source = this.CancellToken.source();

  async getQuizCategories(): Promise<Array<QuizCategoryInfo>> {
    const response = await this.fpAxios.get<Array<QuizCategoryInfoDTO>>(this.quizUrl);
    return response.data.map(x => new QuizCategoryInfo(x));
  }

  async getNewestQuizTestQuestions(category: QuizCategory): Promise<QuizTest> {
    const response = await this.fpAxios.get(`${this.quizUrl}/${category}`);
    return new QuizTest(response.data);
  }

  /** Konkretna wersja testu/oświadczenia — potrzebna, gdy pokazujemy próbę złożoną na starszej wersji. */
  async getQuizTestQuestionsByVersion(category: QuizCategory, version: number): Promise<QuizTest> {
    const response = await this.fpAxios.get(`${this.quizUrl}/${category}/${version}`);
    return new QuizTest(response.data);
  }

  async submitQuizTest(body: QuizTestBody) {
    const response = await this.fpAxios.post(`${this.quizUrl}/attempts`, body);
    return response.data;
  }

  async getLatestResultForCurrentUser(category: QuizCategory): Promise<QuizTestAttempt> {
    const response = await this.fpAxios.get(`${this.quizUrl}/${category}/attempts/my`);
    return new QuizTestAttempt(response.data);
  }

  async getAllLatestAttemptsForCurrentUser(category: QuizCategory): Promise<QuizTestAttempt> {
    const response = await this.fpAxios.get(`${this.quizUrl}/${category}/attempts/latest`)
    return response.data
  }

  async generateXLS(category: string) {
    const response = await this.fpAxios.post(`${this.quizUrl}/${category}/generate-xls-report`);
    return response.data;
  }

  async getNewestAttemptsForEachUser(category: QuizCategory, queryFilters: Pick<QuizAttemptFilter, 'isHrLocked' | 'isContractOfMandate'>): Promise<Array<QuizTestAttempt>> {
    let isCancelled = false;
    if (typeof this.source !== typeof undefined) {
      this.source?.cancel();
    }
    this.source = axios.CancelToken.source();
    const response = this.fpAxios.get(`${this.quizUrl}/${category}/attempts/newest`, { cancelToken: this.source.token, params: queryFilters, },).then((response) => {
      const quizAttempts = response.data.map((testAttempt: QuizTestAttempt) => {
        testAttempt.user = new QuizUser(testAttempt.user);
        return new QuizTestAttempt(testAttempt);
      });
      return quizAttempts || [];
    }).catch(e => {
      if (!axios.isCancel(e)) throw e;
      else {
        isCancelled = true;
        throw isCancelled;
      }
    });
    return response;
  }

  async getTestAttempts(filters: QuizAttemptFilter): Promise<QuizList> {
    const response = await this.fpAxios.post(`${this.quizUrl}/attempts/search`, filters);
    return new QuizList(response.data);
  }

  async getMandateContractUsers(mandateContractUsersFilter:
    MandateContractUserFilters): Promise<MandateContractList> {
    const response = await this.fpAxios.post<MandateContractList>(`${this.usersUrl}/mandate-contract/false`, mandateContractUsersFilter);
    return new MandateContractList(response.data);
  }

  async getMandateContractUsersXls(mandateContractUsersFilter:
    MandateContractUserFilters): Promise<string> {
    const response = await this.fpAxios.post<{ message: string }>(`${this.usersUrl}/mandate-contract/true`, mandateContractUsersFilter,);
    return response
      .data.message;
  }

  async getMandateContractExcelReport(fileId: string): Promise<Blob> {
    const response = await this.fpAxios.get(`files/${fileId}`, {
      responseType: 'blob',
    });
    return new Blob([response.data,], { type: 'application/excel', });
  }
}

export default new HumanResourcesApi();
